// src/lib/capture/drainCaptures.ts — reconnect/foreground auto-drain for queued audio captures.
//
// Closes the offline-resilience "automatic recovery on reconnect" loop for the capture queue
// (the integration point useReconnectDrain deliberately left open). On reconnect/resume the mobile
// shell calls drainCaptureQueue(): it promotes previously-failed items back to a drainable state
// (within a bounded retry budget) and re-drives each one through the SAME
// createMobileSource -> uploadResumable -> finalizeUpload sequence CaptureSheet uses online.
//
// Safe to call repeatedly. Idempotent + resume-safe:
//   • createMobileSource is idempotent on capture_uuid (returns the existing source on re-run);
//   • uploadResumable resumes from the TUS Upload-Offset, so a partially-uploaded blob continues
//     rather than restarting from zero and never creates a duplicate object;
//   • finalizeUpload is idempotent server-side on the source.
// So re-running an item that got part-way through never duplicates work or data.
//
// Consent integrity: the full create context (consent envelope + source args + the owning user id)
// is persisted in the queue item's `meta` at enqueue time (encrypted at rest via the confidential
// store backing offlineQueue), so a headless drain reconstructs createMobileSource exactly as the
// foreground flow would — with the same legal consent basis. Fail-closed: an item that is not
// attributable to the currently signed-in user (different OR missing ownerId), or a manual_audio item
// with no consent envelope, is parked `failed` rather than re-driven (defensive: logout/remote-wipe
// should purgeQueue, but a consent-bearing capture must never be uploaded under a different identity
// or with no consent basis).

import { supabase } from '@/integrations/supabase/client';
import {
  list,
  updateState,
  remove,
  drainPending,
  type QueueItem,
} from '@/lib/native/offlineQueue';
import { uploadResumable } from '@/lib/native/resumableUpload';
import { audioExtensionForMime } from './audioMime';
import { createMobileSource } from './createMobileSource';
import { finalizeUpload } from './finalizeUpload';
import type { Consent } from './types';

// Stop retrying after this many failures so a permanently-bad capture (e.g. consent rejected
// server-side, a corrupt blob) does not loop on every reconnect. The item is left `failed` for a
// future "review failed captures" UI rather than deleted (never silently drop captured audio).
const MAX_DRAIN_ATTEMPTS = 6;

// A mid-flight item (uploading/finalizing) touched more recently than this is probably being driven
// right now by an open CaptureSheet; the drain leaves it alone to avoid racing the foreground upload
// (which would collide on the TUS `x-upsert:false` object). An item touched longer ago is treated as
// abandoned (e.g. the app was killed mid-upload) and recovered.
const ACTIVE_HANDOFF_MS = 30_000;

/** The re-drive context CaptureSheet persists in a queued audio item's `meta`. All optional so a
 *  malformed/legacy item degrades to sensible defaults rather than throwing. */
export interface CaptureQueueMeta {
  /** uuid of the user who captured this — guards against cross-user re-drive. */
  ownerId?: string;
  mode?: 'record' | 'memo';
  durationMs?: number;
  /** research_sources.source_type for createMobileSource. */
  sourceType?: string;
  title?: string;
  rawMetadata?: Record<string, unknown>;
  /** Consent envelope captured at record time — required for third-party audio. */
  consent?: Consent;
  /** research_events.id this capture was tagged to (conference/sub-event), persisted at enqueue
   *  time so a headless reconnect drain tags the source identically to the foreground flow. */
  eventId?: string | null;
}

// Build the storage object key for a queued capture. The extension is derived from the item's stored
// contentType via the SAME audioExtensionForMime helper CaptureSheet uses (native → .m4a, web →
// .webm), so a reconnect re-drive lands on the identical key shape as the foreground upload would.
function objectPathFor(ownerId: string, sourceId: string, contentType: string | undefined): string {
  return `mobile/${ownerId}/${sourceId}/audio${audioExtensionForMime(contentType)}`;
}

// Single-flight guard: the queue is drained from several triggers (mount/login, network reconnect,
// app foreground) which can fire close together. Two concurrent drains would both pick up the same
// pending item and the second upload would collide on the TUS `x-upsert:false` object (HTTP 409).
// Serialise so only one drain runs at a time; overlapping calls no-op and the next trigger re-runs.
let draining = false;

/**
 * Re-drive ONE queued capture to completion. Thrown errors propagate to drainPending, which marks
 * the item `failed` (attempts++); the next drain requeues it until the attempt budget is spent.
 * `requeuedIds` are the items this drain pass just promoted failed->pending_upload (so the recency
 * skip below doesn't defer them as if a foreground handler were driving them).
 */
async function uploadOne(
  item: QueueItem,
  blob: Blob | null,
  ownerId: string,
  requeuedIds: ReadonlySet<string>,
): Promise<void> {
  const meta = (item.meta ?? {}) as CaptureQueueMeta;

  // Fail-closed attribution: never re-drive a consent-bearing capture under a different identity, and
  // treat a MISSING ownerId as not-ours too. Park it `failed` (not a silent no-op) so it leaves the
  // drainable set and isn't re-read on every reconnect; the rightful owner's owner-gated requeue can
  // still recover it later, and logout/remote-wipe purges it. Never `remove` — that could drop another
  // user's un-uploaded consented audio before their own session clears it.
  if (meta.ownerId !== ownerId) {
    await updateState(item.id, 'failed', {
      lastError: 'not attributable to the current user — not re-driven on this device',
    });
    return;
  }

  // Fail-closed consent gate: never re-drive third-party (manual_audio) audio without the consent
  // envelope it was captured under. Park it rather than upload audio with no consent basis.
  const sourceType = meta.sourceType ?? 'manual_audio';
  if (sourceType === 'manual_audio' && !meta.consent) {
    await updateState(item.id, 'failed', {
      lastError: 'missing consent envelope — audio not re-driven',
    });
    return;
  }

  // Leave alone an item a foreground CaptureSheet is probably driving right now (recently touched and
  // NOT one we just requeued this pass) so we don't race its create/upload — this covers the whole
  // enqueue -> create -> uploading window, not just mid-upload. A genuinely abandoned item ages past
  // the hand-off window and is recovered on a later drain.
  if (!requeuedIds.has(item.id) && Date.now() - item.updatedAt < ACTIVE_HANDOFF_MS) {
    return;
  }

  if (!blob) {
    if (item.hasBlob) {
      // The payload was expected but unreadable this pass (transient filesystem / store error). Do
      // NOT delete — throw so drainPending marks it failed and a later drain retries. Never lose
      // captured audio to a transient read error.
      throw new Error('drainCaptures: capture blob expected but unreadable — will retry');
    }
    // Genuinely no payload (nothing was ever written): drop it so it cannot loop forever — a source
    // with no audio object would only strand server-side.
    await remove(item.id);
    return;
  }

  // 1. Ensure the server source exists (idempotent on capture_uuid).
  let sourceId = item.sourceId;
  if (!sourceId) {
    const res = await createMobileSource({
      captureUuid: item.id,
      sourceType,
      title: meta.title ?? (meta.mode === 'memo' ? 'Voice memo' : 'Meeting recording'),
      rawMetadata: meta.rawMetadata ?? (meta.mode === 'memo' ? { capture_profile: 'memo' } : {}),
      consent: meta.consent,
      // Carry the event tag through reconnect so a queued capture lands on the right event.
      eventId: meta.eventId ?? null,
    });
    sourceId = res.sourceId;
    // Guard against a soft-failure that returned no id — never build a `mobile/<uid>/null/…` path.
    if (!sourceId || typeof sourceId !== 'string') {
      throw new Error('drainCaptures: createMobileSource returned no sourceId');
    }
    await updateState(item.id, 'uploading', {
      sourceId,
      objectPath: objectPathFor(ownerId, sourceId, item.contentType),
    });
  }

  const objectPath = item.objectPath ?? objectPathFor(ownerId, sourceId, item.contentType);

  // 2. Resume the upload unless we already know it finished (state === 'finalizing' means the bytes
  //    are up and only finalize is left). TUS resumes from the saved offset; a fully-uploaded blob
  //    resolves quickly.
  if (item.state !== 'finalizing') {
    await updateState(item.id, 'uploading', { sourceId, objectPath });
    await uploadResumable({ data: blob, objectPath, contentType: item.contentType });
  }

  // 3. Finalize → enqueues processing (idempotent server-side).
  await updateState(item.id, 'finalizing', { sourceId });
  await finalizeUpload({ sourceId, captureUuid: item.id });

  // 4. Done — clear the index entry + its blob.
  await updateState(item.id, 'done', { sourceId });
  await remove(item.id);
}

/**
 * Promote failed captures back to a drainable state (within the retry budget) then drain everything
 * drainable. No-op when offline (drainPending checks reachability) or signed out. Returns the number
 * of items handed to the uploader. Never throws (callers treat it as fire-and-forget).
 */
export async function drainCaptureQueue(): Promise<number> {
  if (draining) return 0;
  draining = true;
  try {
    // Attribute uploads to the current user; bail if signed out (nothing to upload as).
    const { data } = await supabase.auth.getSession();
    const ownerId = data.session?.user?.id;
    if (!ownerId) return 0;

    // Requeue previously-failed items (OURS only) so they get another attempt — bounded so a
    // permanently-failing capture stops looping. updateState only increments `attempts` on the
    // ->failed transition, so failed->pending_upload preserves the accumulated count and the budget
    // converges. Owner-gating the requeue means another user's parked items are never re-driven here.
    const items = await list();
    const requeued = new Set<string>();
    for (const it of items) {
      const m = (it.meta ?? {}) as CaptureQueueMeta;
      if (
        it.state === 'failed' &&
        m.ownerId === ownerId &&
        (it.attempts ?? 0) < MAX_DRAIN_ATTEMPTS
      ) {
        await updateState(it.id, 'pending_upload');
        requeued.add(it.id);
      }
    }

    return await drainPending((item, blob) => uploadOne(item, blob, ownerId, requeued));
  } catch {
    // Defensive: the reconnect trigger must never break on a drain error.
    return 0;
  } finally {
    draining = false;
  }
}
